import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, styled, Typography, useTheme } from '@mui/material';
import { IconExternalLink, IconTag } from '@tabler/icons-react';

import { Tabs, Tooltip } from '@zendesk/forethought-elements';
import { NA } from '../constants';
import { TextDivider } from '../ConversationDrawer';
import ConversationTranscript from '../ConversationTranscript';
import {
  RevampConversationTranscript,
  TranscriptContainer,
} from '../ConversationTranscript/RevampConversationTranscript';
import {
  ArticleTranscriptComponent,
  isArticleCardComponent,
  isRelatedArticlesTranscriptComponent,
} from '../ConversationTranscript/types';
import { formatCSAT, formatTimestamp } from '../helpers';
import { useAiQaTabs } from '../hooks/useAiQaTabs';
import useGetCSATColor from '../hooks/useGetCSATColor';
import useIsDeflectionInsightsEnabled from '../hooks/useIsDeflectionInsightsEnabled';
import useIsProactiveConversationsEnabled from '../hooks/useIsProactiveConversationsEnabled';
import { useIsSentimentInsightsEnabled } from '../hooks/useIsSentimentInsightsEnabled';
import { UpdateIntentDialog } from '../UpdateIntentDialog';
import { AiInstructionsSection } from './AiInstructionsSection';
import AiQaChatDrawerTabContent from './AiQaChatDrawerTabContent';
import { ArticlesSection } from './ArticlesSection';
import { ChatEventsSection } from './ChatEventsSection';
import { ContextVariables } from './ContextVariables';
import { DeflectionInsightsData } from './DeflectionInsightData';
import { InlineData } from './InlineData';
import {
  ResolutionSummaryData,
  ResolutionTierInsightsData,
} from './ResolutionTierInsightsData';
import { SummaryCard } from './SummaryCard';
import { formatTimeSpent } from './utils';
import { VerdictCard } from './VerdictCard';
import useOrgConfig from 'src/hooks/discover/useOrgConfig';
import { useIsFeatureFlagEnabled } from 'src/hooks/hooks';
import { useCanEditConversationReview } from 'src/hooks/useChannelAccess';
import { useIsHumanEvalsEnabled } from 'src/hooks/useHumanEvalsGates';
import { SentimentInsightCell } from 'src/pages/intent-conversation-analytics/components/SentimentInsightCell';
import { Conversation } from 'src/services/apiInterfaces';
import { ConversationReviewRating } from 'src/services/insights/types';
import { useGetProactiveTriggersQuery } from 'src/services/proactive-triggers/proactiveTriggerApi';
import { useGetIntentsQuery } from 'src/services/workflow-builder-metrics/workflowBuilderMetricsApi';
import {
  channelToDisplayName,
  ConversationChannel,
} from 'src/types/workflowBuilderAPITypes';

// revamped transcript requires archive components that are added after this date, hence
// only show revamped transcript if the start datetime of the conversation is greater then this date
const REVAMPED_TRANSCRIPT_START_DATETIME = new Date('2025-01-09T00:00:00.000Z');

export const RevampDrawerBody = ({
  onCreateNewInstruction,
  onOpenFeedbackPanel,
  selectedConversation,
}: {
  onCreateNewInstruction: (newId: string) => void;
  onOpenFeedbackPanel: (
    initialRating?: ConversationReviewRating,
    initialComment?: string | null,
  ) => void;
  selectedConversation: Conversation;
}) => {
  const { t } = useTranslation('intent-conversation-analytics');
  const {
    ab_test_samples,
    additional_feedback: csatAdditionalFeedback,
    ai_qa_scores,
    channel: conversationChannel,
    context_variables,
    conversation_id,
    conversation_type,
    csat_feedback,
    csat_resolve,
    csat_score,
    deflected,
    ending_sentiment_score,
    initial_intent_id,
    is_flagged,
    last_executed_intent_user_query,
    proactive_engaged,
    proactive_origin,
    proactive_trigger_id,
    quick_feedback_responses,
    resolution_automated,
    resolution_tier,
    resolution_tier_insights,
    start_datetime,
    summary,
    tagged_zendesk_ticket_source_id,
    tagged_zendesk_ticket_source_link,
    time_spent_in_widget,
    transcript,
    transcript_components,
    user_interactions,
    user_queries,
    workflow_tag,
  } = selectedConversation;

  const { palette } = useTheme();
  const getCSATColor = useGetCSATColor();
  const isDeflectionInsightsEnabled = useIsDeflectionInsightsEnabled();
  const isSentimentInishgtsEnabled = useIsSentimentInsightsEnabled();
  const isResolutionUiEnabled = useIsFeatureFlagEnabled('resolution_ui');
  const {
    feature_flags: dashboardFeatureFlags = [],
    human_evals_supported_channels: humanEvalsSupportedChannels = [],
  } = useOrgConfig();
  const isHumanEvalsEnabled = useIsHumanEvalsEnabled();
  const canEditReview = useCanEditConversationReview(conversationChannel);
  const isDisplaySummaryCardEnabled = dashboardFeatureFlags.includes(
    'display_summary_card',
  );
  // BE mirrors REVIEW_ENABLED_CHANNELS here — empty when human_evals is off.
  const isReviewEnabledChannel =
    !!conversationChannel &&
    humanEvalsSupportedChannels.includes(conversationChannel);
  const { selectedTab, setSelectedTab, showAiQaTab, tabs } =
    useAiQaTabs(ai_qa_scores);

  // Proactive conversation metadata (Type / Proactive trigger / Initial
  // intent). Only rendered for proactive conversations of orgs with the
  // proactive_conversations feature flag; the lookups are skipped otherwise.
  const isProactiveConversationsEnabled = useIsProactiveConversationsEnabled();
  const isProactiveConversation =
    isProactiveConversationsEnabled &&
    conversation_type === 'proactive_conversation';
  const shouldShowProactiveTrigger =
    isProactiveConversation &&
    (!!proactive_trigger_id || proactive_origin === 'proactive_prompt');
  const { data: proactiveTriggersData } = useGetProactiveTriggersQuery(
    undefined,
    { skip: !isProactiveConversation || !proactive_trigger_id },
  );
  const { data: intentsData } = useGetIntentsQuery(
    { product: 'workflow_builder' },
    { skip: !isProactiveConversation || !initial_intent_id },
  );
  const proactiveTriggerName =
    proactiveTriggersData?.proactive_triggers.find(
      trigger => trigger.proactive_trigger_id === proactive_trigger_id,
    )?.name ??
    // A proactive-prompt conversation has no trigger; label it as such
    // instead of showing an empty value. Unresolvable ids fall back to the
    // raw id so the row is still actionable.
    (proactive_origin === 'proactive_prompt' && !proactive_trigger_id
      ? t('revampDrawerBody.proactivePromptValue')
      : (proactive_trigger_id ?? NA));
  const initialIntentName = initial_intent_id
    ? (intentsData?.intents.find(
        intent => intent.intent_definition_id === initial_intent_id,
      )?.intent_name ?? initial_intent_id)
    : NA;

  const csatFeedback = csat_feedback?.join(', ');

  const getCsatResolve = () => {
    if (typeof csat_resolve == 'boolean') {
      return csat_resolve ? 'Yes' : 'No';
    }
    return 'n/a';
  };

  const articleTranscriptComponents: ArticleTranscriptComponent[] =
    useMemo(() => {
      const result: ArticleTranscriptComponent[] = [];

      transcript_components?.forEach(comp => {
        if (
          isArticleCardComponent(comp) ||
          isRelatedArticlesTranscriptComponent(comp)
        ) {
          result.push(comp);
        }
      });

      return result;
    }, [transcript_components]);

  return (
    <Box p='40px' width='100%'>
      <Box py='6px'>
        <Typography variant='font24'>{t('drawerContent.title')}</Typography>
      </Box>
      <Box display='flex' flexDirection='column' gap='30px' py='24px'>
        {/* Info section */}
        <Box display='flex' flexDirection='column' gap='12px'>
          <InlineData
            label={t('emailConversationDrawer.conversationId')}
            value={conversation_id}
          />
          {isProactiveConversationsEnabled && (
            <InlineData
              label={t('revampDrawerBody.sourceLabel')}
              value={
                isProactiveConversation
                  ? t('revampDrawerBody.sourceProactiveValue')
                  : t('revampDrawerBody.sourceUserInitiatedValue')
              }
            />
          )}
          {isResolutionUiEnabled ? (
            <ResolutionSummaryData
              deflected={deflected}
              resolutionAutomated={resolution_automated}
              resolutionTier={resolution_tier}
              resolutionTierInsights={resolution_tier_insights}
            />
          ) : (
            <InlineData
              label={t('drawerBody.deflectedLabel')}
              value={deflected ? 'True' : 'False'}
            />
          )}
          {isProactiveConversation && (
            <InlineData
              label={t('revampDrawerBody.engagedLabel')}
              value={
                proactive_engaged
                  ? t('revampDrawerBody.engagedYesValue')
                  : t('revampDrawerBody.engagedNoValue')
              }
            />
          )}
        </Box>
        {isDisplaySummaryCardEnabled && summary && (
          <SummaryCard conversationId={conversation_id} summary={summary} />
        )}
        {isHumanEvalsEnabled && isReviewEnabledChannel && (
          <VerdictCard
            conversationId={conversation_id}
            isFlagged={is_flagged}
            isReadOnly={!canEditReview}
            onOpenFeedbackPanel={onOpenFeedbackPanel}
          />
        )}
        {/* Live chat id section */}
        {tagged_zendesk_ticket_source_id &&
          tagged_zendesk_ticket_source_link && (
            <Box display='flex' flexDirection='column' gap='2px'>
              <Typography variant='font11'>
                {t('revampDrawerBody.liveConversationIdLabel')}
              </Typography>
              <Typography
                color={palette.colors.grey[600]}
                variant='font12Medium'
              >
                <a
                  href={tagged_zendesk_ticket_source_link}
                  rel='noreferrer'
                  target='_blank'
                >
                  <Box alignItems='center' display='flex' gap='2px'>
                    {tagged_zendesk_ticket_source_id}
                    <IconExternalLink size={14} />
                  </Box>
                </a>
              </Typography>
            </Box>
          )}
        {/* Tabs section */}
        <Box display='flex' flexDirection='column' gap='10px'>
          {showAiQaTab && (
            <Tabs
              a11yProps={index => ({
                'aria-controls': `conversation-drawer-tabpanel-${index}`,
                id: `conversation-drawer-tab-${index}`,
              })}
              aria-label={t('revampDrawerBody.conversationDrawerTabsAriaLabel')}
              onChange={(_, value) => setSelectedTab(value)}
              tabs={tabs}
              typographyVariant='font14'
              value={selectedTab}
            />
          )}
          {selectedTab === 0 && (
            <>
              {!showAiQaTab && (
                <Typography variant='font11'>
                  {t('emailConversationDrawer.conversation')}
                </Typography>
              )}
              <Card>
                <CardSection>
                  <InlineData
                    label={t('conversationsCard.timeCreatedLabel')}
                    value={formatTimestamp(start_datetime + 'Z')}
                  />
                  <InlineData
                    label={t('revampDrawerBody.timeSpentLabel')}
                    value={formatTimeSpent(time_spent_in_widget)}
                  />
                  <InlineData
                    label={t('revampDrawerBody.channelLabel')}
                    value={
                      channelToDisplayName[
                        conversationChannel as ConversationChannel
                      ] ?? conversationChannel
                    }
                  />
                  {isProactiveConversation && (
                    <>
                      {shouldShowProactiveTrigger && (
                        <InlineData
                          label={t('revampDrawerBody.proactiveTriggerLabel')}
                          value={proactiveTriggerName}
                        />
                      )}
                      {initial_intent_id && (
                        <InlineData
                          label={t('revampDrawerBody.initialIntentLabel')}
                          value={initialIntentName}
                        />
                      )}
                    </>
                  )}
                  {workflow_tag && (
                    <InlineData
                      label={t('drawerBody.workflowTagLabel')}
                      value={
                        <Tooltip placement='top' tooltipContent={workflow_tag}>
                          <Box
                            sx={{
                              alignItems: 'center',
                              bgcolor: palette.colors.slate[100],
                              borderRadius: '4px',
                              color: palette.colors.grey[800],
                              display: 'flex',
                              overflowX: 'auto',
                              px: 1,
                              py: 0.5,
                            }}
                          >
                            <IconTag
                              color={palette.colors.blue[500]}
                              size={16}
                            />
                            <Typography
                              color={palette.colors.black}
                              noWrap
                              variant='font12'
                            >
                              {workflow_tag}
                            </Typography>
                          </Box>
                        </Tooltip>
                      }
                    />
                  )}
                  {isResolutionUiEnabled ? (
                    <ResolutionSummaryData
                      deflected={deflected}
                      resolutionAutomated={resolution_automated}
                      resolutionTier={resolution_tier}
                      resolutionTierInsights={resolution_tier_insights}
                    />
                  ) : (
                    <InlineData
                      label={t('drawerBody.deflectedLabel')}
                      value={deflected ? 'True' : 'False'}
                    />
                  )}
                  <InlineData
                    label={t('revampDrawerBody.userInteractionsLabel')}
                    value={user_interactions}
                  />
                  {isSentimentInishgtsEnabled && (
                    <InlineData
                      label={t('filterOptions.sentiment')}
                      value={
                        <SentimentInsightCell
                          fontVariant='font12'
                          sentimentScore={ending_sentiment_score}
                          timestamp={start_datetime}
                        />
                      }
                    />
                  )}
                  {
                    <InlineData
                      color={getCSATColor(csat_score)}
                      label={t('drawerBody.csatLabel')}
                      value={formatCSAT(csat_score)}
                    />
                  }
                </CardSection>

                <Divider />
                {context_variables && (
                  <ContextVariables contextVariables={context_variables} />
                )}

                <Divider />
                <CardSection>
                  <Typography color={palette.colors.grey[600]} variant='font12'>
                    {t('revampDrawerBody.userQueriesLabel')}
                  </Typography>
                  {user_queries ? (
                    <Box display='flex' flexDirection='column'>
                      {user_queries.map((query, index) => (
                        <i key={index}>
                          <Typography
                            color={palette.colors.blue[600]}
                            sx={{
                              minWidth: 0,
                              overflowWrap: 'anywhere',
                              whiteSpace: 'pre-wrap',
                              width: '100%',
                              wordBreak: 'break-all',
                            }}
                            variant='font14'
                          >
                            {query}
                          </Typography>
                        </i>
                      ))}
                    </Box>
                  ) : (
                    <i>
                      <Typography
                        color={palette.colors.blue[600]}
                        sx={{
                          minWidth: 0,
                          overflowWrap: 'anywhere',
                          whiteSpace: 'pre-wrap',
                          width: '100%',
                          wordBreak: 'break-all',
                        }}
                        variant='font14'
                      >
                        {t('common.quotedValue', {
                          value: last_executed_intent_user_query,
                        })}
                      </Typography>
                    </i>
                  )}
                </CardSection>

                {transcript_components && (
                  <>
                    <Divider />
                    <ChatEventsSection
                      abTestSamples={ab_test_samples}
                      onCreateNewInstruction={onCreateNewInstruction}
                      transcriptComponents={transcript_components}
                    />
                  </>
                )}

                {isResolutionUiEnabled && (
                  <>
                    <Divider />
                    <CardSection>
                      <ResolutionTierInsightsData
                        resolutionTier={resolution_tier}
                        resolutionTierInsights={resolution_tier_insights}
                      />
                    </CardSection>
                  </>
                )}

                {
                  <>
                    <Divider />
                    <CardSection>
                      <InlineData
                        color={getCSATColor(csat_score)}
                        label={t('drawerBody.csatLabel')}
                        value={formatCSAT(csat_score)}
                      />
                      <InlineData
                        label={t('csatSubmissionStep.reasonsForRating')}
                        value={
                          <i>
                            <Typography
                              color={
                                !!csatFeedback
                                  ? palette.colors.blue[600]
                                  : palette.colors.grey[600]
                              }
                              variant='font12'
                            >
                              {csatFeedback || NA}
                            </Typography>
                          </i>
                        }
                      />
                      <InlineData
                        label={t('csatSubmissionStep.additionalFeedback')}
                        value={
                          <i>
                            <Typography
                              color={
                                !!csatAdditionalFeedback
                                  ? palette.colors.blue[600]
                                  : palette.colors.grey[600]
                              }
                              variant='font12'
                            >
                              {csatAdditionalFeedback || NA}
                            </Typography>
                          </i>
                        }
                      />
                      <InlineData
                        label={t('csatSubmissionStep.resolutionConfirmation')}
                        value={getCsatResolve()}
                      />
                    </CardSection>
                  </>
                }
                {quick_feedback_responses &&
                  quick_feedback_responses.length > 0 && (
                    <CardSection>
                      <ArticlesSection
                        onCreateNewInstruction={onCreateNewInstruction}
                        quickFeedbackResponses={quick_feedback_responses}
                        transcriptComponents={articleTranscriptComponents}
                      />
                    </CardSection>
                  )}
                {transcript_components &&
                  transcript_components.some(
                    component =>
                      component.message_type === 'ai_instructions' ||
                      component.message_type === 'content_snippet',
                  ) && (
                    <>
                      <Divider />
                      <AiInstructionsSection
                        onCreateNewInstruction={onCreateNewInstruction}
                        transcriptComponents={transcript_components}
                      />
                    </>
                  )}

                {isDeflectionInsightsEnabled && (
                  <CardSection>
                    <DeflectionInsightsData
                      selectedConversation={selectedConversation}
                    />
                  </CardSection>
                )}

                {
                  <CardSection gap='16px'>
                    <TextDivider
                      size='small'
                      text={t('drawerBody.reassignTextDivider')}
                    />
                    <UpdateIntentDialog
                      userQuery={
                        user_queries && user_queries.length > 0
                          ? user_queries[0]
                          : last_executed_intent_user_query || ''
                      }
                    />
                  </CardSection>
                }
              </Card>
              {transcript_components &&
              new Date(start_datetime) > REVAMPED_TRANSCRIPT_START_DATETIME ? (
                <RevampConversationTranscript
                  proactiveTriggerName={
                    shouldShowProactiveTrigger
                      ? proactiveTriggerName
                      : undefined
                  }
                  transcriptComponents={transcript_components}
                />
              ) : (
                <Box display='flex' flexDirection='column' gap='10px'>
                  <Typography variant='font11'>
                    {t('revampConversationTranscript.title')}
                  </Typography>
                  <TranscriptContainer>
                    <ConversationTranscript
                      transcript={transcript}
                      variant='drawer'
                    />
                  </TranscriptContainer>
                </Box>
              )}
            </>
          )}
          {selectedTab === 1 && showAiQaTab && ai_qa_scores && (
            <AiQaChatDrawerTabContent aiQaScores={ai_qa_scores} />
          )}
        </Box>
      </Box>
    </Box>
  );
};

const Divider = styled('hr')`
  border-width: 0;
  border-bottom-width: thin;
  border-color: ${({ theme }) => theme.palette.colors.slate[200]};
  width: 100%;
  margin-top: 8px;
  margin-bottom: 8px;
`;

const Card = styled(Box)`
  border: 1px solid ${({ theme }) => theme.palette.colors.slate[200]};
  border-radius: 8px;
  /* padding: 16px; */
  display: flex;
  flex-direction: column;
`;

const CardSection = styled(Box)<{ gap?: string }>`
  display: flex;
  flex-direction: column;
  padding: 16px;
  ${props => props.gap && `gap: ${props.gap}`}
`;
